'use client';

import React, { useState, useCallback } from 'react';
import SearchTopBar from './SearchTopBar';
import FilterSidebar from './FilterSidebar';
import SearchResults from './SearchResults';
import MapPanel from './MapPanel';
import { SlidersHorizontal, Map, List } from 'lucide-react';

export type ViewMode = 'list' | 'map' | 'split';

export interface SearchFilters {
  listingType: 'satilik' | 'kiralik' | 'all';
  propertyType: string[];
  priceMin: string;
  priceMax: string;
  areaMin: string;
  areaMax: string;
  bedrooms: string[];
  bathrooms: string[];
  buildingAge: string;
  floor: string;
  heating: string[];
  features: string[];
  sortBy: string;
  city: string;
  district: string;
  neighborhood: string;
}

const DEFAULT_FILTERS: SearchFilters = {
  listingType: 'satilik',
  propertyType: [],
  priceMin: '',
  priceMax: '',
  areaMin: '',
  areaMax: '',
  bedrooms: [],
  bathrooms: [],
  buildingAge: '',
  floor: '',
  heating: [],
  features: [],
  sortBy: 'onerilen',
  city: '',
  district: '',
  neighborhood: '',
};

export default function PropertySearchClient() {
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);

  const updateFilter = useCallback(<K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Top Search Bar */}
      <SearchTopBar
        filters={filters}
        updateFilter={updateFilter}
        onOpenFilters={() => setIsSidebarOpen(true)}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Filter Sidebar — desktop always visible, mobile drawer */}
        <aside
          className={`
            flex-shrink-0 bg-white border-r border-border overflow-y-auto
            transition-all duration-300 ease-in-out
            hidden lg:block w-72 xl:w-80
          `}
        >
          <FilterSidebar
            filters={filters}
            updateFilter={updateFilter}
            resetFilters={resetFilters}
          />
        </aside>

        {/* Mobile Filter Drawer */}
        {isSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div
              className="flex-1 bg-black/40"
              onClick={() => setIsSidebarOpen(false)}
            />
            <div className="w-80 bg-white overflow-y-auto animate-slide-up shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal size={18} className="text-primary" />
                  <span className="font-bold text-foreground">Filtreler</span>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <FilterSidebar
                filters={filters}
                updateFilter={updateFilter}
                resetFilters={resetFilters}
              />
            </div>
          </div>
        )}

        {/* Results area */}
        <div className="flex-1 flex overflow-hidden">
          {/* List — show in list or split mode */}
          {(viewMode === 'list' || viewMode === 'split') && (
            <div className={`overflow-y-auto ${viewMode === 'split' ? 'w-full lg:w-[52%] xl:w-[48%] 2xl:w-[45%]' : 'w-full'}`}>
              <SearchResults
                filters={filters}
                updateFilter={updateFilter}
                selectedPropertyId={selectedPropertyId}
                onSelectProperty={setSelectedPropertyId}
              />
            </div>
          )}

          {/* Map — show in map or split mode */}
          {(viewMode === 'map' || viewMode === 'split') && (
            <div className={`${viewMode === 'split' ? 'hidden lg:block flex-1' : 'w-full'} relative`}>
              <MapPanel
                selectedPropertyId={selectedPropertyId}
                onSelectProperty={setSelectedPropertyId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile view toggle */}
      <div className="lg:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-foreground rounded-full px-4 py-2 shadow-xl">
        <button
          onClick={() => setViewMode('list')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === 'list' ? 'bg-white text-foreground' : 'text-white/70 hover:text-white'}`}
        >
          <List size={14} />
          Liste
        </button>
        <button
          onClick={() => setViewMode('map')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === 'map' ? 'bg-white text-foreground' : 'text-white/70 hover:text-white'}`}
        >
          <Map size={14} />
          Harita
        </button>
      </div>
    </div>
  );
}